import {
    faArrowUpRight,
    faDownload,
    faEye,
} from "@fortawesome/pro-light-svg-icons";
import { faTriangleExclamation } from "@fortawesome/pro-solid-svg-icons";
import type {
    GridColDef,
    GridColumnHeaderParams,
    GridRenderCellParams,
    GridRowParams,
} from "@mui/x-data-grid";
import { DataGrid, GridActionsCellItem } from "@mui/x-data-grid";
import Typography from "@vapor/react-extended/ExtendedTypography";
import { EllipsisVertical, VaporIcon } from "@vapor/react-icons";
import { Box, CircularProgress, useTheme } from "@vapor/react-material";
import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import type { JSX } from "react/jsx-runtime";

import { useDownloadTaxSubmissionF24Pdf } from "../../../../../core/api/use-download-tax-submission-f24-pdf/useDownloadTaxSubmissionF24Pdf";
import { useDownloadTaxSubmissionOutcomeMutation } from "../../../../../core/api/use-download-tax-submission-outcome/useDownloadTaxSubmissionOutcomeMutation";
import { useDownloadTaxSubmissionPdfOutcomeMutation } from "../../../../../core/api/use-download-tax-submission-pdf-outcome/useDownloadTaxSubmissionPdfOutcomeMutation";
import type {
    OutcomeTypePrefix,
    TaxPayer,
} from "../../../../../core/api/use-get-tax-payers/FulfilmentDTO";
import {
    GovernmentAgencyType,
    type TaxSubmissionDetailDTO,
} from "../../../../../core/api/use-get-tax-submission-detail/TaxSubmissionDetailDTO";
import { FulfilmentStatus } from "../../../../../core/dtos/FulfilmentStatus";
import { downloadFile } from "../../../../../core/utils/downloadFile";
import { FulfilmentStatusTypography } from "../../../../components/fulfilment-status-typography/FulfilmentStatusTypography";
import { getTranslation } from "../../../../utils/outcomesTypes";
import { createNavigationUrl } from "../../../../utils/resolveUrn";

export interface RowModel {
    id: string;
    taxSubmissionId: string;
    taxpayer: TaxPayer;
    taxPayerPosition?: number;
    delegationPosition?: number;
    taxPayerPath?: string;
    protocol?: string;
    status: FulfilmentStatus;
    amount?: string;
    outcomeTypes?: string[];
    isRevocable: boolean;
    revoked?: boolean;
}

interface TaxSubmissionDetailTaxPayersTableProps {
    loading: boolean;
    onClickPreviewOutcome: (
        availablesOutcomeTypes: string[],
        governmentAgencyType: GovernmentAgencyType,
        taxSubmissionId: string,
        selectedOutcomeType: string,
        taxPayer?: string,
    ) => unknown;
    onClickF24Preview: (
        taxSubmissionId: string,
        taxPayerId: string,
        taxPayerName: string,
        taxPayerPosition: number,
        delegationPosition?: number,
    ) => void;
    onRowSelectionModelChange: (fulfilmentIds: string[]) => unknown;
    revokeTaxSubmissionsEnabled: boolean;
    selectedFulfilmentIds: string[];
    taxSubmissionDetail: TaxSubmissionDetailDTO;
}

export const TaxSubmissionDetailTaxPayersTable: React.FC<
    TaxSubmissionDetailTaxPayersTableProps
> = ({
    loading,
    onRowSelectionModelChange,
    onClickF24Preview,
    onClickPreviewOutcome,
    revokeTaxSubmissionsEnabled,
    selectedFulfilmentIds,
    taxSubmissionDetail,
}): JSX.Element => {
    const { t } = useTranslation();
    const theme = useTheme();
    const navigate = useNavigate();

    const downloadPdfOutcomeMutation =
        useDownloadTaxSubmissionPdfOutcomeMutation();
    const downloadOutcomeMutation = useDownloadTaxSubmissionOutcomeMutation();
    const downloadF24PdfUseCase = useDownloadTaxSubmissionF24Pdf();
    const [downloadingActions, setDownloadingActions] = useState(new Set());

    const setDownloadActionPending = useCallback(
        (key: string, loading: boolean) => {
            setDownloadingActions((prev) => {
                const newDownloadingActions = new Set(prev);

                if (loading) newDownloadingActions.add(key);
                else newDownloadingActions.delete(key);

                return newDownloadingActions;
            });
        },
        [],
    );

    const rows = useMemo<RowModel[]>(
        () =>
            taxSubmissionDetail
                ? taxSubmissionDetail.fulfilments.map((fulfilment) => ({
                      id: fulfilment.id,
                      taxSubmissionId: taxSubmissionDetail.id,
                      taxpayer: fulfilment.taxpayer,
                      taxPayerPosition: fulfilment.taxPayerPosition,
                      delegationPosition: fulfilment.delegationPosition,
                      taxPayerPath: fulfilment.taxPayerPath,
                      protocol: fulfilment.protocol,
                      status: fulfilment.status,
                      amount: fulfilment.amount,
                      outcomeTypes: fulfilment.outcomeTypes,
                      isRevocable: fulfilment.isRevocable,
                      revoked: fulfilment.revoked,
                  }))
                : [],
        [taxSubmissionDetail],
    );

    const onClickDownloadPdfOutcome = useCallback(
        async (
            _: React.MouseEvent<HTMLElement>,
            taxSubmissionId: string,
            outcomeType: string,
            taxPayer?: string,
        ) => {
            try {
                const resourceData =
                    taxSubmissionDetail.governmentAgency.type ===
                    GovernmentAgencyType.ade
                        ? await downloadPdfOutcomeMutation.mutateAsync({
                              taxSubmissionId,
                              outcomeTypes: [outcomeType],
                              taxId: taxPayer,
                          })
                        : await downloadOutcomeMutation.mutateAsync({
                              taxSubmissionId,
                              outcomeType,
                              taxId: taxPayer,
                          });
                downloadFile(
                    resourceData.content,
                    `${outcomeType}_${taxPayer}`,
                    resourceData.type,
                );
            } catch {
                // error notification already shown by handleHttpError
            }
        },
        [
            downloadOutcomeMutation,
            downloadPdfOutcomeMutation,
            taxSubmissionDetail.governmentAgency.type,
        ],
    );

    const onClickDownloadF24Pdf = useCallback(
        async (
            taxpayer: string,
            taxPayerPosition: number,
            delegationPosition?: number,
        ) => {
            try {
                const response = await downloadF24PdfUseCase.mutateAsync({
                    taxSubmissionId: taxSubmissionDetail.id,
                    taxPayerId: taxpayer,
                    taxPayerPosition,
                    delegationPosition,
                });
                downloadFile(response.content, taxpayer, response.type);
            } catch {
                // error notification already shown by handleHttpError
            }
        },
        [downloadF24PdfUseCase, taxSubmissionDetail.id],
    );

    const goToProcedureButton = useCallback(
        (path: string) => [
            <GridActionsCellItem
                key="procedure-button"
                icon={<VaporIcon icon={faArrowUpRight} color="interactive" />}
                label={t(
                    "views.home.components.tax_submissions_table.actions.go_to_procedure",
                )}
                style={{ color: theme.palette.primary.interactiveDefault }}
                onClick={() => navigate(createNavigationUrl(path))}
                showInMenu
            />,
        ],
        [navigate, t, theme.palette.primary.interactiveDefault],
    );

    const getActions = useCallback(
        (
            actionType: "preview" | "download",
            outcomeTypes: string[],
            taxSubmissionId: string,
            taxpayer: string,
        ): JSX.Element[] => {
            const outcomeCount: Record<string, number> = {};
            return outcomeTypes.map((outcomeType) => {
                const prefix = (
                    outcomeType.length === 3
                        ? outcomeType
                        : outcomeType.substring(0, 3)
                ) as OutcomeTypePrefix;

                const actionKey = `${taxSubmissionId}-${taxpayer}-${actionType}-${prefix}-${outcomeCount[prefix]}`;
                const loading = downloadingActions.has(actionKey);

                outcomeCount[prefix] = (outcomeCount[prefix] || 0) + 1;
                const numeral =
                    outcomeCount[prefix] > 1 ? ` ${outcomeCount[prefix]}` : "";
                const label = `${getTranslation(prefix, actionType, t)}${numeral}`;

                const handleClick = (
                    event: React.MouseEvent<HTMLLIElement>,
                ) => {
                    event.stopPropagation();

                    if (actionType === "download") {
                        const runDownload = async () => {
                            setDownloadActionPending(actionKey, true);

                            try {
                                await onClickDownloadPdfOutcome(
                                    event,
                                    taxSubmissionId,
                                    outcomeType,
                                    taxpayer,
                                );
                            } catch (error) {
                                console.error(
                                    "Error downloading outcome pdf",
                                    error,
                                );
                            } finally {
                                setDownloadActionPending(actionKey, false);
                            }
                        };

                        void runDownload();
                    } else {
                        onClickPreviewOutcome(
                            outcomeTypes,
                            taxSubmissionDetail.governmentAgency.type,
                            taxSubmissionId,
                            outcomeType,
                            taxpayer,
                        );
                    }
                };

                return (
                    <GridActionsCellItem
                        key={actionKey}
                        closeMenuOnClick={false}
                        disabled={loading}
                        icon={
                            loading ? (
                                <CircularProgress size={14} />
                            ) : (
                                <VaporIcon
                                    icon={
                                        actionType === "download"
                                            ? faDownload
                                            : faEye
                                    }
                                    color="interactive"
                                />
                            )
                        }
                        label={label}
                        style={{
                            color: theme.palette.primary.interactiveDefault,
                        }}
                        onClick={handleClick}
                        showInMenu
                    />
                );
            });
        },
        [
            downloadingActions,
            onClickDownloadPdfOutcome,
            onClickPreviewOutcome,
            setDownloadActionPending,
            taxSubmissionDetail.governmentAgency.type,
            t,
            theme.palette.primary.interactiveDefault,
        ],
    );

    const getF24Action = useCallback(
        (
            type: "preview" | "download",
            taxPayerId: string,
            taxPayerName: string,
            taxPayerPosition: number,
            delegationPosition?: number,
        ): JSX.Element => {
            const actionKey = `${type}-${taxPayerId}-${taxPayerPosition}`;
            const loading = downloadingActions.has(actionKey);

            const handleClick = (event: React.MouseEvent<HTMLLIElement>) => {
                event.stopPropagation();

                if (type === "download") {
                    const runDownload = async () => {
                        setDownloadActionPending(actionKey, true);

                        try {
                            await onClickDownloadF24Pdf(
                                taxPayerId,
                                taxPayerPosition,
                                delegationPosition,
                            );
                        } catch (error) {
                            console.error("Error downloading F24 pdf", error);
                        } finally {
                            setDownloadActionPending(actionKey, false);
                        }
                    };

                    void runDownload();
                } else {
                    onClickF24Preview(
                        taxSubmissionDetail.id,
                        taxPayerId,
                        taxPayerName,
                        taxPayerPosition,
                        delegationPosition,
                    );
                }
            };

            return (
                <GridActionsCellItem
                    key={actionKey}
                    closeMenuOnClick={false}
                    icon={
                        loading ? (
                            <CircularProgress size={14} />
                        ) : (
                            <VaporIcon
                                icon={type === "download" ? faDownload : faEye}
                                color="interactive"
                            />
                        )
                    }
                    label={t(
                        `views.home.components.tax_submission_detail_tax_payers_table.${type}_f24_pdf_preview`,
                    )}
                    style={{
                        color: theme.palette.primary.interactiveDefault,
                    }}
                    disabled={loading}
                    onClick={handleClick}
                    showInMenu
                />
            );
        },
        [
            downloadingActions,
            onClickDownloadF24Pdf,
            onClickF24Preview,
            setDownloadActionPending,
            taxSubmissionDetail.id,
            t,
            theme.palette.primary.interactiveDefault,
        ],
    );

    const renderHeader = useCallback(
        (params: GridColumnHeaderParams) => (
            <Typography
                color={theme.palette.primary.textTitleColor}
                variant="body500"
                noWrap
            >
                {params.colDef.headerName}
            </Typography>
        ),
        [theme.palette.primary.textTitleColor],
    );

    const columns = useMemo<GridColDef[]>(
        () => [
            {
                field: "taxpayer",
                flex: 0.6,
                headerName: t(
                    "views.home.components.tax_submission_detail_tax_payers_table.header.taxpayer",
                ),
                renderHeader,
                renderCell: (params: GridRenderCellParams<RowModel>) => (
                    <Box
                        display="flex"
                        flexDirection="row"
                        gap={1}
                        alignItems="center"
                    >
                        {params.row.status ===
                            FulfilmentStatus.PaymentError && (
                            <VaporIcon
                                icon={faTriangleExclamation}
                                color="warning"
                            />
                        )}
                        <Typography variant="body" noWrap>
                            {params.row.taxpayer.name}
                        </Typography>
                    </Box>
                ),
            },
            {
                field: "protocol",
                flex: 0.4,
                headerName: t(
                    "views.home.components.tax_submission_detail_tax_payers_table.header.protocol",
                ),
                renderHeader,
                renderCell: (params) => (
                    <Typography variant="body" noWrap>
                        {params.value || "-"}
                    </Typography>
                ),
            },
            {
                field: "status",
                flex: 0.5,
                headerName: t(
                    "views.home.components.tax_submission_detail_tax_payers_table.header.status",
                ),
                renderHeader,
                renderCell: (params: GridRenderCellParams<RowModel>) => (
                    <FulfilmentStatusTypography
                        status={params.value}
                        taxSubmissionType={taxSubmissionDetail.type}
                        taxSubmissionStatus={taxSubmissionDetail.status}
                        outcomeTypes={taxSubmissionDetail.outcomeTypes}
                        revoked={params.row.revoked}
                    />
                ),
            },
            {
                field: "amount",
                headerAlign: "right",
                align: "right",
                flex: 0.3,
                headerName: t(
                    "views.home.components.tax_submission_detail_tax_payers_table.header.amount",
                ),
                renderHeader,
                renderCell: (params) => (
                    <Typography variant="body" noWrap>
                        {params.value ? `${params.value} €` : "-"}
                    </Typography>
                ),
            },
            {
                field: "menu",
                type: "actions",
                flex: 0.1,
                getActions: (params: GridRowParams<RowModel>) => {
                    const actions: {
                        type: string;
                        action: JSX.Element;
                    }[] = [];
                    const fulfilment = params.row;

                    if (
                        taxSubmissionDetail.type === "F24A0" &&
                        fulfilment.taxPayerPosition !== undefined &&
                        fulfilment.delegationPosition !== undefined
                    ) {
                        actions.push({
                            type: "preview",
                            action: getF24Action(
                                "preview",
                                fulfilment.taxpayer.taxId,
                                fulfilment.taxpayer.name,
                                fulfilment.taxPayerPosition,
                                fulfilment.delegationPosition,
                            ),
                        });
                        actions.push({
                            type: "download",
                            action: getF24Action(
                                "download",
                                fulfilment.taxpayer.taxId,
                                fulfilment.taxpayer.name,
                                fulfilment.taxPayerPosition,
                                fulfilment.delegationPosition,
                            ),
                        });
                    }

                    if (fulfilment.outcomeTypes && fulfilment.taxpayer)
                        actions.push(
                            ...getActions(
                                "preview",
                                fulfilment.outcomeTypes,
                                fulfilment.taxSubmissionId,
                                fulfilment.taxpayer.taxId,
                            ).map((action) => ({ type: "preview", action })),
                            ...getActions(
                                "download",
                                fulfilment.outcomeTypes,
                                fulfilment.taxSubmissionId,
                                fulfilment.taxpayer.taxId,
                            ).map((action) => ({ type: "download", action })),
                        );

                    const sortedAction = actions
                        .sort((a, b) => a.type.localeCompare(b.type))
                        .map(({ action }) => action);

                    return fulfilment.taxPayerPath
                        ? [
                              ...sortedAction,
                              ...goToProcedureButton(fulfilment.taxPayerPath),
                          ]
                        : sortedAction;
                },
            },
        ],
        [
            getActions,
            getF24Action,
            goToProcedureButton,
            renderHeader,
            t,
            taxSubmissionDetail.outcomeTypes,
            taxSubmissionDetail.status,
            taxSubmissionDetail.type,
        ],
    );

    return (
        <DataGrid
            autoHeight
            checkboxSelection={revokeTaxSubmissionsEnabled}
            columns={columns}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnMenu
            hideFooter
            hideFooterPagination
            // This is specific to enable selection when generating a revocation,
            // if we need the selection for other purposes we should change this logic
            isRowSelectable={(params) => params.row.isRevocable}
            onRowSelectionModelChange={(ids) => {
                onRowSelectionModelChange(ids.map((id) => id.toString()));
            }}
            loading={loading}
            localeText={{
                noRowsLabel: t(
                    "views.home.components.tax_submission_detail_tax_payers_table.no_data",
                ),
            }}
            rows={rows}
            rowSelectionModel={selectedFulfilmentIds}
            slots={{
                moreActionsIcon: (props) => (
                    <EllipsisVertical {...props} color="interactive" />
                ),
            }}
            sx={{ border: "none", width: "calc(100% - 10px)" }}
        />
    );
};
